import { useState, useRef, useCallback, useEffect } from "react";

// ── Constants ─────────────────────────────────────────────────────────────────
const GRID_SIZE = 20;
const CANVAS_W = 2400;
const CANVAS_H = 1800;

const KINGDOM_COLORS = ["#c0392b","#e67e22","#f1c40f","#2ecc71","#1abc9c","#3498db","#9b59b6","#e91e63","#ff5722","#00bcd4"];
const CHAR_COLORS    = ["#e74c3c","#3498db","#2ecc71","#f39c12","#9b59b6","#1abc9c","#e91e63","#34495e"];
const ARMY_COLORS    = ["#64748b","#dc2626","#d97706","#16a34a","#2563eb","#7c3aed","#db2777","#0f172a"];

const CITY_STATUSES  = ["normal","under_attack","besieged"];
const STATUS_COLORS  = { normal:"#22c55e", under_attack:"#f97316", besieged:"#ef4444" };
const STATUS_LABELS  = { normal:"Normal", under_attack:"Under Attack", besieged:"Besieged" };

// ── Helpers ───────────────────────────────────────────────────────────────────
const snapToGrid = (x, y) => [Math.round(x / GRID_SIZE) * GRID_SIZE, Math.round(y / GRID_SIZE) * GRID_SIZE];
const coordStr   = (x, y) => `${x},${y}`;
const parseCoord = (s)     => { const [x, y] = s.split(",").map(Number); return { x, y }; };

function distToSegment(px, py, [ax, ay], [bx, by]) {
  const dx = bx - ax, dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

function pointInPolygon(px, py, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i], [xj, yj] = polygon[j];
    if (((yi > py) !== (yj > py)) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi)
      inside = !inside;
  }
  return inside;
}

// ── API ───────────────────────────────────────────────────────────────────────
const API_BASE = "https://6jmdsp6ama.execute-api.us-east-1.amazonaws.com/prod";

async function apiGet() {
  const res = await fetch(`${API_BASE}/data`);
  if (!res.ok) throw new Error(`GET failed: ${res.status}`);
  return res.json(); // { kingdoms, cities, characters, armies }
}

async function apiPut(item) {
  // DynamoDB rejects null values — strip them out before sending
  const clean = Object.fromEntries(
    Object.entries(item).filter(([, v]) => v !== null && v !== undefined && v !== "")
  );
  const res = await fetch(`${API_BASE}/data`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ item: clean }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`PUT failed: ${res.status} — ${detail}`);
  }
  return res.json();
}

async function apiDelete(primary_key, sort_key) {
  const res = await fetch(`${API_BASE}/data`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: primary_key, sort_key }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`DELETE failed: ${res.status} — ${detail}`);
  }
  return res.json();
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
const inputStyle = {
  background:"#f9fafb", border:"1px solid #e5e7eb", color:"#111827",
  padding:"5px 8px", borderRadius:5, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box",
};
const selectStyle = { ...inputStyle, cursor:"pointer" };
const mkBtn = (bg) => ({
  background:bg, color:"#fff", border:"none", borderRadius:5,
  padding:"6px 12px", cursor:"pointer", fontSize:13, fontWeight:500, flex:1,
});

function Field({ label, children }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
      <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", letterSpacing:0.5, textTransform:"uppercase" }}>{label}</label>
      {children}
    </div>
  );
}
function FInput(props) { return <input {...props} style={inputStyle} />; }

function Panel({ title, onClose, children }) {
  return (
    <div style={{
      position:"absolute", top:0, right:0, bottom:0, width:300,
      background:"#fff", borderLeft:"1px solid #e5e7eb",
      boxShadow:"-2px 0 12px #0000001a", display:"flex", flexDirection:"column", zIndex:20, overflowY:"auto",
    }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 14px", borderBottom:"1px solid #f3f4f6", flexShrink:0 }}>
        <span style={{ fontWeight:600, fontSize:14, color:"#111827" }}>{title}</span>
        <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#9ca3af" }}>✕</button>
      </div>
      <div style={{ padding:"12px 14px", display:"flex", flexDirection:"column", gap:10 }}>
        {children}
      </div>
    </div>
  );
}

// Computed read-only badge
function Badge({ label, value, color }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:5, padding:"4px 8px" }}>
      <span style={{ fontSize:12, color:"#6b7280" }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:600, color: color || "#111827" }}>{value}</span>
    </div>
  );
}

// Draggable entity row inside city panel
function CityEntityRow({ item, type, onDragOut }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 6px", background:"#f9fafb", borderRadius:5, border:"1px solid #e5e7eb", marginBottom:3 }}>
      <span style={{
        width:10, height:10, background:item.color, flexShrink:0, display:"inline-block",
        borderRadius: type === "army" ? 2 : "50%",
        transform: type === "army" ? "rotate(45deg)" : "none",
      }} />
      <span style={{ fontSize:12, color:"#374151", flex:1 }}>{item.name}{item.role ? ` · ${item.role}` : ""}{item.size ? ` (${item.size.toLocaleString()})` : ""}</span>
      <button
        title="Move out to map"
        onClick={() => onDragOut(item.primary_key, type)}
        style={{ background:"none", border:"1px solid #d1d5db", borderRadius:4, cursor:"pointer", fontSize:10, color:"#6b7280", padding:"2px 5px" }}>
        ↗ Map
      </button>
    </div>
  );
}

// ── Name Modal ────────────────────────────────────────────────────────────────
function NameModal({ type, onConfirm, onCancel }) {
  const [value, setValue] = useState("");
  const [size, setSize] = useState("");
  const [role, setRole] = useState("");
  const isArmy = type === "army";
  const isChar = type === "character";

  function handleConfirm() {
    if (!value.trim()) return;
    onConfirm({ name: value.trim(), size: isArmy ? (parseInt(size) || 0) : undefined, role: isChar ? role.trim() : undefined });
  }

  const titles = { kingdom:"Name this Kingdom", city:"Name this City", character:"New Character", army:"New Army" };

  return (
    <div style={{
      position:"fixed", inset:0, background:"#00000055", zIndex:100,
      display:"flex", alignItems:"center", justifyContent:"center",
    }}>
      <div style={{ background:"#fff", borderRadius:10, padding:24, width:300, boxShadow:"0 8px 32px #0003" }}>
        <div style={{ fontWeight:600, fontSize:15, color:"#111827", marginBottom:14 }}>{titles[type]}</div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <Field label="Name">
            <FInput autoFocus value={value} onChange={e => setValue(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleConfirm(); if (e.key === "Escape") onCancel(); }}
              placeholder={type === "kingdom" ? "e.g. Kingdom of Valdor" : type === "city" ? "e.g. Ironhold" : type === "character" ? "e.g. Ser Aldric" : "e.g. First Legion"} />
          </Field>
          {isChar && (
            <Field label="Role">
              <FInput value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. noble, warrior, merchant..." />
            </Field>
          )}
          {isArmy && (
            <Field label="Size">
              <FInput value={size} onChange={e => setSize(e.target.value)} type="number" placeholder="e.g. 5000" />
            </Field>
          )}
        </div>
        <div style={{ display:"flex", gap:8, marginTop:16 }}>
          <button onClick={handleConfirm} style={mkBtn("#3b82f6")}>Place</button>
          <button onClick={onCancel} style={mkBtn("#9ca3af")}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── City Panel ────────────────────────────────────────────────────────────────
function CityPanel({ city, characters, armies, kingdoms, onUpdate, onClose, onDelete, onMoveOut }) {
  const [name, setName]           = useState(city.name);
  const [allegiance, setAllegiance] = useState(city.allegiance || "");
  const [status, setStatus]       = useState(city.status || "normal");
  const [notes, setNotes]         = useState(city.notes || "");

  // Computed from linked entities
  const cityChars   = characters.filter(c => c.city === city.primary_key);
  const cityArmies  = armies.filter(a => a.city === city.primary_key);
  const garrison    = cityArmies.reduce((sum, a) => sum + (a.size || 0), 0);

  return (
    <Panel title={`● ${city.name}`} onClose={onClose}>
      <Field label="Name"><FInput value={name} onChange={e => setName(e.target.value)} /></Field>
      <Field label="Allegiance">
        <select value={allegiance} onChange={e => setAllegiance(e.target.value)} style={selectStyle}>
          <option value="">— None —</option>
          {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
        </select>
      </Field>
      <Field label="Status">
        <select value={status} onChange={e => setStatus(e.target.value)} style={selectStyle}>
          {CITY_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
      </Field>

      {/* Computed */}
      <Field label="Garrison (computed)">
        <Badge label="Total troops" value={garrison.toLocaleString()} color="#2563eb" />
      </Field>

      <Field label={`Armies here (${cityArmies.length})`}>
        {cityArmies.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : cityArmies.map(a => <CityEntityRow key={a.primary_key} item={a} type="army" onDragOut={onMoveOut} />)
        }
      </Field>

      <Field label={`Characters here (${cityChars.length})`}>
        {cityChars.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : cityChars.map(c => <CityEntityRow key={c.primary_key} item={c} type="character" onDragOut={onMoveOut} />)
        }
      </Field>

      <Field label="Notes">
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          rows={3} style={{ ...inputStyle, resize:"vertical" }} placeholder="Lore, description..." />
      </Field>
      <div style={{ display:"flex", gap:6, marginTop:4 }}>
        <button onClick={() => onUpdate({ ...city, name, allegiance, status, notes })} style={mkBtn("#3b82f6")}>Save</button>
        <button onClick={onDelete} style={mkBtn("#ef4444")}>Delete</button>
      </div>
    </Panel>
  );
}

// ── Kingdom Panel ─────────────────────────────────────────────────────────────
function KingdomPanel({ kingdom, characters, armies, cities, onUpdate, onClose, onDelete, onEditBorders }) {
  const [name, setName]   = useState(kingdom.name);
  const [notes, setNotes] = useState(kingdom.notes || "");

  // Computed
  const kCities  = cities.filter(c => c.allegiance === kingdom.primary_key);
  const kArmies  = armies.filter(a => a.allegiance === kingdom.primary_key);
  const totalTroops = kArmies.reduce((sum, a) => sum + (a.size || 0), 0);
  const kChars   = characters.filter(c => c.allegiance === kingdom.primary_key);

  return (
    <Panel title={`▨ ${kingdom.name}`} onClose={onClose}>
      <Field label="Kingdom Name"><FInput value={name} onChange={e => setName(e.target.value)} /></Field>

      <Field label="Summary">
        <Badge label="Cities" value={kCities.length} />
        <div style={{ height:4 }} />
        <Badge label="Armies" value={kArmies.length} color="#dc2626" />
        <div style={{ height:4 }} />
        <Badge label="Total Troops" value={totalTroops.toLocaleString()} color="#dc2626" />
        <div style={{ height:4 }} />
        <Badge label="Characters" value={kChars.length} color="#7c3aed" />
      </Field>

      <Field label={`Cities (${kCities.length})`}>
        {kCities.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : kCities.map(c => (
            <div key={c.primary_key} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#374151", marginBottom:3 }}>
              <span style={{ width:8, height:8, borderRadius:"50%", background: STATUS_COLORS[c.status] || "#9ca3af", display:"inline-block" }} />
              {c.name} <span style={{ fontSize:11, color:"#9ca3af" }}>({STATUS_LABELS[c.status]})</span>
            </div>
          ))
        }
      </Field>

      <Field label={`Armies (${kArmies.length})`}>
        {kArmies.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : kArmies.map(a => (
            <div key={a.primary_key} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#374151", marginBottom:3 }}>
              <span style={{ width:10, height:10, background:a.color, display:"inline-block", borderRadius:2, transform:"rotate(45deg)", flexShrink:0 }} />
              {a.name}{a.size ? ` — ${a.size.toLocaleString()}` : ""}
            </div>
          ))
        }
      </Field>

      <Field label={`Characters (${kChars.length})`}>
        {kChars.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None</span>
          : kChars.map(c => (
            <div key={c.primary_key} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:"#374151", marginBottom:3 }}>
              <span style={{ width:10, height:10, borderRadius:"50%", background:c.color, display:"inline-block", flexShrink:0 }} />
              {c.name}{c.role ? ` · ${c.role}` : ""}
            </div>
          ))
        }
      </Field>

      <Field label="Notes">
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          rows={3} style={{ ...inputStyle, resize:"vertical" }} placeholder="History, lore..." />
      </Field>
      <div style={{ display:"flex", gap:6, marginTop:4 }}>
        <button onClick={() => onUpdate({ ...kingdom, name, notes })} style={mkBtn("#3b82f6")}>Save</button>
        <button onClick={onDelete} style={mkBtn("#ef4444")}>Delete</button>
      </div>
      <button onClick={() => onEditBorders(kingdom)}
        style={{ background:"#7c3aed", color:"#fff", border:"none", borderRadius:5, padding:"6px 12px", cursor:"pointer", fontSize:13, fontWeight:500, width:"100%", marginTop:4 }}>
        ✏ Edit Borders
      </button>
    </Panel>
  );
}

// ── Character Panel ───────────────────────────────────────────────────────────
function CharacterPanel({ character, cities, kingdoms, armies, onUpdate, onClose, onDelete }) {
  const [name, setName]           = useState(character.name);
  const [role, setRole]           = useState(character.role || "");
  const [allegiance, setAllegiance] = useState(character.allegiance || "");
  const [army, setArmy]           = useState(character.army || "");
  const [notes, setNotes]         = useState(character.notes || "");
  const [color, setColor]         = useState(character.color || CHAR_COLORS[0]);
  const [cityVal, setCityVal]     = useState(character.city || "");

  // location display
  const locationDesc = character.city
    ? `In ${cities.find(c => c.primary_key === character.city)?.name || character.city}`
    : character.coordinates ? `Map (${character.coordinates})` : "Unknown";

  return (
    <Panel title={`👤 ${character.name}`} onClose={onClose}>
      <Field label="Name"><FInput value={name} onChange={e => setName(e.target.value)} /></Field>
      <Field label="Role"><FInput value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. noble, warrior..." /></Field>
      <Field label="Allegiance">
        <select value={allegiance} onChange={e => setAllegiance(e.target.value)} style={selectStyle}>
          <option value="">— None —</option>
          {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
        </select>
      </Field>
      <Field label="Army (if general)">
        <select value={army} onChange={e => setArmy(e.target.value)} style={selectStyle}>
          <option value="">— None —</option>
          {armies.map(a => <option key={a.primary_key} value={a.primary_key}>{a.name}</option>)}
        </select>
      </Field>
      <Field label="City">
        <select value={cityVal} onChange={e => setCityVal(e.target.value)} style={selectStyle}>
          <option value="">— On map —</option>
          {cities.map(c => <option key={c.primary_key} value={c.primary_key}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Current Location">
        <Badge label="Location" value={locationDesc} />
      </Field>
      <Field label="Color">
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
          {CHAR_COLORS.map(c => (
            <div key={c} onClick={() => setColor(c)} style={{
              width:20, height:20, borderRadius:"50%", background:c, cursor:"pointer",
              border: color === c ? "2px solid #111" : "2px solid transparent",
            }} />
          ))}
        </div>
      </Field>
      <Field label="Notes">
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          rows={3} style={{ ...inputStyle, resize:"vertical" }} placeholder="Background, goals..." />
      </Field>
      <div style={{ display:"flex", gap:6, marginTop:4 }}>
        <button onClick={() => onUpdate({ ...character, name, role, allegiance, army, notes, color, city: cityVal })} style={mkBtn("#3b82f6")}>Save</button>
        <button onClick={onDelete} style={mkBtn("#ef4444")}>Delete</button>
      </div>
    </Panel>
  );
}

// ── Army Panel ────────────────────────────────────────────────────────────────
function ArmyPanel({ army, characters, cities, kingdoms, onUpdate, onClose, onDelete, onMoveOut }) {
  const [name, setName]           = useState(army.name);
  const [allegiance, setAllegiance] = useState(army.allegiance || "");
  const [general, setGeneral]     = useState(army.general || "");
  const [size, setSize]           = useState(army.size || 0);
  const [notes, setNotes]         = useState(army.notes || "");
  const [color, setColor]         = useState(army.color || ARMY_COLORS[0]);
  const [cityVal, setCityVal]     = useState(army.city || "");

  const locationDesc = army.city
    ? `In ${cities.find(c => c.primary_key === army.city)?.name || army.city}`
    : army.coordinates ? `Map (${army.coordinates})` : "Unknown";

  const armyChars = characters.filter(c => c.army === army.primary_key);

  return (
    <Panel title={`⚔ ${army.name}`} onClose={onClose}>
      <Field label="Army Name"><FInput value={name} onChange={e => setName(e.target.value)} /></Field>
      <Field label="Size">
        <FInput value={size} onChange={e => setSize(parseInt(e.target.value) || 0)} type="number" />
      </Field>
      <Field label="Allegiance">
        <select value={allegiance} onChange={e => setAllegiance(e.target.value)} style={selectStyle}>
          <option value="">— None —</option>
          {kingdoms.map(k => <option key={k.primary_key} value={k.primary_key}>{k.name}</option>)}
        </select>
      </Field>
      <Field label="General">
        <select value={general} onChange={e => setGeneral(e.target.value)} style={selectStyle}>
          <option value="">— None —</option>
          {characters.map(c => <option key={c.primary_key} value={c.primary_key}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="City">
        <select value={cityVal} onChange={e => setCityVal(e.target.value)} style={selectStyle}>
          <option value="">— On map —</option>
          {cities.map(c => <option key={c.primary_key} value={c.primary_key}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Current Location">
        <Badge label="Location" value={locationDesc} />
      </Field>
      <Field label="Token Color">
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
          {ARMY_COLORS.map(c => (
            <div key={c} onClick={() => setColor(c)} style={{
              width:20, height:20, borderRadius:3, background:c, cursor:"pointer",
              border: color === c ? "2px solid #111" : "2px solid transparent",
            }} />
          ))}
        </div>
      </Field>
      <Field label={`Characters (${armyChars.length})`}>
        {armyChars.length === 0
          ? <span style={{ fontSize:12, color:"#9ca3af" }}>None enlisted</span>
          : armyChars.map(c => <CityEntityRow key={c.primary_key} item={c} type="character" onDragOut={(id) => onMoveOut(id, "fromArmy")} />)
        }
      </Field>
      <Field label="Notes">
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          rows={3} style={{ ...inputStyle, resize:"vertical" }} placeholder="Composition, notes..." />
      </Field>
      <div style={{ display:"flex", gap:6, marginTop:4 }}>
        <button onClick={() => onUpdate({ ...army, name, allegiance, general, size, notes, color, city: cityVal })} style={mkBtn("#3b82f6")}>Save</button>
        <button onClick={onDelete} style={mkBtn("#ef4444")}>Delete</button>
      </div>
    </Panel>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function T() {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 800, h: 600 });
  const [tool, setTool]           = useState("select");
  const [selectedColor, setSelectedColor] = useState(KINGDOM_COLORS[2]);

  // Schema-aligned state
  // Kingdom:   { primary_key, sort_key:"Kingdom", name, color, points:[[x,y],...], notes }
  // City:      { primary_key, sort_key:"City", name, allegiance, status, coordinates:"x,y", notes, color(ui) }
  // Character: { primary_key, sort_key:"Character", name, role, allegiance, army, city, coordinates, notes, color }
  // Army:      { primary_key, sort_key:"army#generalName", name, allegiance, city, coordinates, size, general, notes, color }
  const [kingdoms,   setKingdoms]   = useState([]);
  const [cities,     setCities]     = useState([]);
  const [characters, setCharacters] = useState([]);
  const [armies,     setArmies]     = useState([]);

  const [drawingPoints, setDrawingPoints] = useState([]);
  const [panel, setPanel]           = useState(null); // { type, id }

  // API state
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showBattle, setShowBattle] = useState(false);

  // ── Initial data load ─────────────────────────────────────────────────────
  useEffect(() => {
    apiGet()
      .then(data => {
        setKingdoms(data.kingdoms   || []);
        setCities(data.cities       || []);
        setCharacters(data.characters || []);
        setArmies(data.armies       || []);
      })
      .catch(err => setApiError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // Modal state: pending placement
  const [modal, setModal] = useState(null); // { type, x, y } — position queued, waiting for name

  // dragging tokens
  const [dragging, setDragging]     = useState(null); // { type:"character"|"army", id }
  const [dragPos,  setDragPos]      = useState(null);

  // kingdom border editing
  const [editingKingdom, setEditingKingdom] = useState(null); // { id, points: [[x,y],...] }
  const [draggingVertex, setDraggingVertex] = useState(null); // index into editingKingdom.points

  const [pan,  setPan]  = useState({ x:0, y:0 });
  const [zoom, setZoom] = useState(1);
  const isPanning  = useRef(false);
  const panStart   = useRef(null);
  const panOrigin  = useRef(null);
  const didPan     = useRef(false);

  const getSVGPoint = useCallback((e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return [(e.clientX - rect.left - pan.x) / zoom, (e.clientY - rect.top - pan.y) / zoom];
  }, [pan, zoom]);

  // Which army token is at map coords (wx,wy)?
  const armyAt = useCallback((wx, wy) => {
    for (const army of armies)
      if (army.coordinates) {
        const { x, y } = parseCoord(army.coordinates);
        if (Math.abs(wx - x) < 16 && Math.abs(wy - y) < 16) return army;
      }
    return null;
  }, [armies]);

  // Which city is at map coords (wx,wy)?
  const cityAt = useCallback((wx, wy) => {
    for (const city of cities)
      if (city.coordinates) {
        const { x, y } = parseCoord(city.coordinates);
        if (Math.abs(wx - x) < 20 && Math.abs(wy - y) < 20) return city;
      }
    return null;
  }, [cities]);

  // Which kingdom polygon contains (wx,wy)?
  const kingdomAt = useCallback((wx, wy) => {
    for (let i = kingdoms.length - 1; i >= 0; i--)
      if (kingdoms[i].points && pointInPolygon(wx, wy, kingdoms[i].points)) return kingdoms[i];
    return null;
  }, [kingdoms]);

  // ── Pan / zoom ────────────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    if (e.button === 1 || tool === "pan") {
      isPanning.current = true; didPan.current = false;
      panStart.current  = { x:e.clientX, y:e.clientY };
      panOrigin.current = { ...pan };
      e.preventDefault();
    }
  }, [tool, pan]);

  const handleMouseMove = useCallback((e) => {
    if (isPanning.current) {
      const dx = e.clientX - panStart.current.x, dy = e.clientY - panStart.current.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didPan.current = true;
      setPan({ x:panOrigin.current.x + dx, y:panOrigin.current.y + dy });
    }
    if (draggingVertex !== null && editingKingdom) {
      const [wx, wy] = getSVGPoint(e);
      const [gx, gy] = snapToGrid(wx, wy);
      setEditingKingdom(ek => {
        const pts = [...ek.points];
        pts[draggingVertex] = [gx, gy];
        return { ...ek, points: pts };
      });
    }
    if (dragging) setDragPos(getSVGPoint(e));
  }, [dragging, draggingVertex, editingKingdom, getSVGPoint]);

  const handleMouseUp = useCallback(async (e) => {
    if (dragging) {
      const [wx, wy] = getSVGPoint(e);
      const [gx, gy] = snapToGrid(wx, wy);
      const nearCity  = cityAt(wx, wy);
      let updated = null;

      const nearArmy = dragging.type === "character" ? armyAt(wx, wy) : null;

      if (dragging.type === "character") {
        setCharacters(cs => cs.map(c => {
          if (c.primary_key !== dragging.id) return c;
          if (nearArmy && nearArmy.primary_key !== dragging.id) {
            updated = { ...c, army: nearArmy.primary_key, coordinates: null };
          } else if (nearCity) {
            updated = { ...c, city: nearCity.primary_key, coordinates: null };
          } else {
            updated = { ...c, city: null, army: null, coordinates: coordStr(gx, gy) };
          }
          return updated;
        }));
      } else {
        setArmies(as => as.map(a => {
          if (a.primary_key !== dragging.id) return a;
          updated = nearCity
            ? { ...a, city: nearCity.primary_key, coordinates: null }
            : { ...a, city: null, coordinates: coordStr(gx, gy) };
          return updated;
        }));
      }
      setDragging(null); setDragPos(null);
      if (draggingVertex !== null) { setDraggingVertex(null); return; }
      if (updated) {
        try { await apiPut(updated); } catch (e) { console.error("Failed to save position:", e); }
      }
    }
    isPanning.current = false;
  }, [dragging, getSVGPoint, cityAt, armyAt]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    const nz = Math.min(4, Math.max(0.3, zoom * factor));
    setZoom(nz);
    setPan({ x:cx - (cx - pan.x) * (nz / zoom), y:cy - (cy - pan.y) * (nz / zoom) });
  }, [zoom, pan]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive:false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // Track container size for scrollbars
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Release vertex drag on window mouseup so it fires even if mouse leaves SVG
  useEffect(() => {
    const onUp = () => { if (draggingVertex !== null) setDraggingVertex(null); };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, [draggingVertex]);

  // ── Click → open modal or draw ────────────────────────────────────────────
  const handleSVGClick = useCallback((e) => {
    if (tool === "pan" || modal) return;
    if (didPan.current) { didPan.current = false; return; }
    const [wx, wy] = getSVGPoint(e);
    const [gx, gy] = snapToGrid(wx, wy);

    // In edit mode — check for click near an edge to insert a new point
    if (editingKingdom) {
      const pts = editingKingdom.points;
      let minDist = Infinity, minIdx = -1;
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        const b = pts[(i + 1) % pts.length];
        const dist = distToSegment(wx, wy, a, b);
        console.log(`edge ${i}: dist=${dist.toFixed(2)}, threshold=${(20/zoom).toFixed(2)}`);
        if (dist < minDist) { minDist = dist; minIdx = i; }
      }
      console.log(`click at wx=${wx.toFixed(1)} wy=${wy.toFixed(1)}, zoom=${zoom}, closest edge=${minIdx} dist=${minDist.toFixed(2)}`);
      if (minDist < 20 / zoom) {
        const newPts = [...pts];
        newPts.splice(minIdx + 1, 0, [gx, gy]);
        setEditingKingdom(ek => ({ ...ek, points: newPts }));
      }
      return;
    }

    if (tool === "draw") {
      if (e.detail === 2 && drawingPoints.length >= 3) {
        // open modal to name kingdom
        setModal({ type:"kingdom", points: drawingPoints.slice(0, -1), color: selectedColor });
        setDrawingPoints([]);
      } else if (e.detail === 1) {
        setDrawingPoints(p => [...p, [gx, gy]]);
      }
      return;
    }
    if (["city","character","army"].includes(tool)) {
      setModal({ type:tool, x:gx, y:gy });
      return;
    }
    if (tool === "select") {
      // hit-test: armies > characters > cities > kingdoms
      for (const a of armies) {
        if (!a.coordinates) continue;
        const { x, y } = parseCoord(a.coordinates);
        if (Math.abs(wx - x) < 14 && Math.abs(wy - y) < 14) { setPanel({ type:"army", id:a.primary_key }); return; }
      }
      for (const ch of characters) {
        if (!ch.coordinates) continue;
        const { x, y } = parseCoord(ch.coordinates);
        if (Math.abs(wx - x) < 14 && Math.abs(wy - y) < 14) { setPanel({ type:"character", id:ch.primary_key }); return; }
      }
      for (const city of cities) {
        if (!city.coordinates) continue;
        const { x, y } = parseCoord(city.coordinates);
        if (Math.abs(wx - x) < 14 && Math.abs(wy - y) < 14) { setPanel({ type:"city", id:city.primary_key }); return; }
      }
      const k = kingdomAt(wx, wy);
      if (k) { setPanel({ type:"kingdom", id:k.primary_key }); return; }
      setPanel(null);
    }
  }, [tool, getSVGPoint, drawingPoints, selectedColor, armies, characters, cities, kingdomAt, modal]);

  // ── Modal confirm ─────────────────────────────────────────────────────────
  const handleModalConfirm = useCallback(async ({ name, size, role }) => {
    const primary_key = `${modal.type}_${Date.now()}`;
    let newItem = null;

    if (modal.type === "kingdom") {
      newItem = { primary_key, sort_key:"Kingdom", name, color: modal.color, points: modal.points, notes:"" };
      setKingdoms(ks => [...ks, newItem]);
    } else if (modal.type === "city") {
      newItem = { primary_key, sort_key:"City", name, allegiance:"", status:"normal", coordinates: coordStr(modal.x, modal.y), notes:"" };
      setCities(cs => [...cs, newItem]);
    } else if (modal.type === "character") {
      const colorIdx = characters.length % CHAR_COLORS.length;
      newItem = { primary_key, sort_key:"Character", name, role: role || "", allegiance:"", army:"", coordinates: coordStr(modal.x, modal.y), notes:"", color: CHAR_COLORS[colorIdx] };
      setCharacters(cs => [...cs, newItem]);
    } else if (modal.type === "army") {
      const colorIdx = armies.length % ARMY_COLORS.length;
      newItem = { primary_key, sort_key:`army#`, name, allegiance:"", general:"", coordinates: coordStr(modal.x, modal.y), size: size || 0, notes:"", color: ARMY_COLORS[colorIdx] };
      setArmies(as => [...as, newItem]);
    }

    setModal(null);

    if (newItem) {
      try { await apiPut(newItem); }
      catch (err) { console.error("Failed to save new item:", err); }
    }
  }, [modal, characters.length, armies.length]);

  // ── Token drag start ──────────────────────────────────────────────────────
  const handleTokenMouseDown = useCallback((e, type, id) => {
    if (tool !== "select") return;
    e.stopPropagation();
    setDragging({ type, id });
    setDragPos(getSVGPoint(e));
  }, [tool, getSVGPoint]);

  // ── Move entity out of city back to map ───────────────────────────────────
  const handleMoveOut = useCallback(async (id, type) => {
    let updated = null;
    if (type === "character") {
      // Moving a character out of a city
      setCharacters(cs => cs.map(c => {
        if (c.primary_key !== id) return c;
        const city = cities.find(ci => ci.primary_key === c.city);
        const base = city?.coordinates ? parseCoord(city.coordinates) : { x:400, y:400 };
        updated = { ...c, city: null, coordinates: coordStr(base.x + 30, base.y + 30) };
        return updated;
      }));
    } else if (type === "fromArmy") {
      // Moving a character out of an army back to map
      setCharacters(cs => cs.map(c => {
        if (c.primary_key !== id) return c;
        const army = armies.find(a => a.primary_key === c.army);
        const base = army?.coordinates ? parseCoord(army.coordinates) : { x:400, y:400 };
        updated = { ...c, army: null, coordinates: coordStr(base.x + 30, base.y + 30) };
        return updated;
      }));
    } else {
      // Moving an army out of a city
      setArmies(as => as.map(a => {
        if (a.primary_key !== id) return a;
        const city = cities.find(ci => ci.primary_key === a.city);
        const base = city?.coordinates ? parseCoord(city.coordinates) : { x:400, y:400 };
        updated = { ...a, city: null, coordinates: coordStr(base.x - 30, base.y + 30) };
        return updated;
      }));
    }
    if (updated) {
      try { await apiPut(updated); } catch (e) { console.error("Failed to move out:", e); }
    }
  }, [cities, armies]);

  // ── Finish kingdom from sidebar button ────────────────────────────────────
  const finishKingdom = useCallback(() => {
    if (drawingPoints.length >= 3)
      setModal({ type:"kingdom", points: drawingPoints, color: selectedColor });
      setDrawingPoints([]);
  }, [drawingPoints, selectedColor]);

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const updateKingdom = useCallback(async (u) => {
    setKingdoms(ks => ks.map(k => k.primary_key === u.primary_key ? u : k));
    try { setSaving(true); await apiPut(u); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, []);
  const updateCity = useCallback(async (u) => {
    setCities(cs => cs.map(c => c.primary_key === u.primary_key ? u : c));
    try { setSaving(true); await apiPut(u); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, []);
  const updateCharacter = useCallback(async (u) => {
    setCharacters(cs => cs.map(c => c.primary_key === u.primary_key ? u : c));
    try { setSaving(true); await apiPut(u); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, []);
  const updateArmy = useCallback(async (u) => {
    setArmies(as => as.map(a => a.primary_key === u.primary_key ? u : a));
    try { setSaving(true); await apiPut(u); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, []);
  const deleteKingdom = useCallback(async (id) => {
    setKingdoms(ks => ks.filter(k => k.primary_key !== id)); setPanel(null);
    try { await apiDelete(id, "Kingdom"); } catch (e) { console.error(e); }
  }, []);
  const deleteCity = useCallback(async (id) => {
    setCities(cs => cs.filter(c => c.primary_key !== id)); setPanel(null);
    try { await apiDelete(id, "City"); } catch (e) { console.error(e); }
  }, []);
  const deleteCharacter = useCallback(async (id) => {
    setCharacters(cs => cs.filter(c => c.primary_key !== id)); setPanel(null);
    try { await apiDelete(id, "Character"); } catch (e) { console.error(e); }
  }, []);
  const deleteArmy = useCallback(async (id, sort_key) => {
    setArmies(as => as.filter(a => a.primary_key !== id)); setPanel(null);
    try { await apiDelete(id, sort_key || "army#"); } catch (e) { console.error(e); }
  }, []);

  // ── Kingdom border edit ──────────────────────────────────────────────────────
  const startEditingKingdom = useCallback((kingdom) => {
    setEditingKingdom({ id: kingdom.primary_key, points: kingdom.points.map(p => [...p]) });
    setPanel(null); // close panel while editing
  }, []);

  const confirmKingdomEdit = useCallback(async () => {
    if (!editingKingdom) return;
    setKingdoms(ks => ks.map(k =>
      k.primary_key === editingKingdom.id ? { ...k, points: editingKingdom.points } : k
    ));
    const updated = kingdoms.find(k => k.primary_key === editingKingdom.id);
    const saved = { ...updated, points: editingKingdom.points };
    setEditingKingdom(null);
    try { setSaving(true); await apiPut(saved); } catch (e) { console.error(e); } finally { setSaving(false); }
  }, [editingKingdom, kingdoms]);

  const cancelKingdomEdit = useCallback(() => {
    setEditingKingdom(null);
    setDraggingVertex(null);
  }, []);

  // ── Grid ──────────────────────────────────────────────────────────────────
  const gridLines = [];
  for (let x = 0; x <= CANVAS_W; x += GRID_SIZE)
    gridLines.push(<line key={`v${x}`} x1={x} y1={0} x2={x} y2={CANVAS_H} stroke="#d0d4d8" strokeWidth="0.7" />);
  for (let y = 0; y <= CANVAS_H; y += GRID_SIZE)
    gridLines.push(<line key={`h${y}`} x1={0} y1={y} x2={CANVAS_W} y2={y} stroke="#d0d4d8" strokeWidth="0.7" />);

  const cursorStyle = editingKingdom ? (draggingVertex !== null ? "grabbing" : "crosshair")
    : tool === "pan" ? "grab" : tool === "draw" ? "crosshair"
    : ["city","character","army"].includes(tool) ? "cell"
    : dragging ? "grabbing" : "default";

  const panelKingdom = panel?.type === "kingdom"   ? kingdoms.find(k   => k.primary_key === panel.id) : null;
  const panelCity    = panel?.type === "city"      ? cities.find(c     => c.primary_key === panel.id) : null;
  const panelChar    = panel?.type === "character" ? characters.find(c => c.primary_key === panel.id) : null;
  const panelArmy    = panel?.type === "army"      ? armies.find(a     => a.primary_key === panel.id) : null;

  // ── Scrollbar calculations ───────────────────────────────────────────────────
  const SCROLL_W = 12; // scrollbar track width
  const scaledW = CANVAS_W * zoom;
  const scaledH = CANVAS_H * zoom;
  // Thumb size as fraction of visible area vs total canvas
  const hThumbRatio = Math.min(1, containerSize.w / scaledW);
  const vThumbRatio = Math.min(1, containerSize.h / scaledH);
  // Clamp pan so thumb doesn't go out of track
  const maxPanX = 0;
  const minPanX = containerSize.w - scaledW;
  const maxPanY = 0;
  const minPanY = containerSize.h - scaledH;
  // Thumb position: how far into the scrollable range are we
  const hScrollRange = scaledW - containerSize.w;
  const vScrollRange = scaledH - containerSize.h;
  const hThumbX = hScrollRange > 0 ? ((-pan.x) / hScrollRange) * (containerSize.w - SCROLL_W) * (1 - hThumbRatio) : 0;
  const vThumbY = vScrollRange > 0 ? ((-pan.y) / vScrollRange) * (containerSize.h - SCROLL_W) * (1 - vThumbRatio) : 0;
  const hThumbW = (containerSize.w - SCROLL_W) * hThumbRatio;
  const vThumbH = (containerSize.h - SCROLL_W) * vThumbRatio;
  const showHScroll = hThumbRatio < 1;
  const showVScroll = vThumbRatio < 1;

  const scrollDrag = useRef(null); // { axis, startMouse, startPan }

  const handleScrollMouseDown = useCallback((e, axis) => {
    e.preventDefault();
    e.stopPropagation();
    scrollDrag.current = { axis, startMouse: axis === "h" ? e.clientX : e.clientY, startPan: { ...pan } };
    const onMove = (me) => {
      if (!scrollDrag.current) return;
      const { axis, startMouse, startPan } = scrollDrag.current;
      if (axis === "h") {
        const trackW = containerSize.w - SCROLL_W;
        const delta = (me.clientX - startMouse) / (trackW * (1 - hThumbRatio));
        const newPanX = Math.min(0, Math.max(minPanX, startPan.x - delta * hScrollRange));
        setPan(p => ({ ...p, x: newPanX }));
      } else {
        const trackH = containerSize.h - SCROLL_W;
        const delta = (me.clientY - startMouse) / (trackH * (1 - vThumbRatio));
        const newPanY = Math.min(0, Math.max(minPanY, startPan.y - delta * vScrollRange));
        setPan(p => ({ ...p, y: newPanY }));
      }
    };
    const onUp = () => { scrollDrag.current = null; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [pan, hThumbRatio, vThumbRatio, hScrollRange, vScrollRange, minPanX, minPanY, containerSize]);

  // Only show tokens for entities NOT in a city
  const mapCharacters = characters.filter(c => !c.city && !c.army && c.coordinates);
  const mapArmies     = armies.filter(a => !a.city && a.coordinates);

  function tokenXY(entity) {
    if (dragging?.id === entity.primary_key && dragPos) return { x:dragPos[0], y:dragPos[1] };
    const { x, y } = parseCoord(entity.coordinates);
    return { x, y };
  }

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", fontFamily:"'Segoe UI', system-ui, sans-serif", flexDirection:"column", gap:12 }}>
      <div style={{ fontSize:28 }}>🗺</div>
      <div style={{ fontSize:14, color:"#6b7280" }}>Loading map data...</div>
    </div>
  );

  if (apiError) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", fontFamily:"'Segoe UI', system-ui, sans-serif", flexDirection:"column", gap:12 }}>
      <div style={{ fontSize:28 }}>⚠️</div>
      <div style={{ fontSize:14, color:"#ef4444", fontWeight:600 }}>Failed to load map data</div>
      <div style={{ fontSize:12, color:"#9ca3af" }}>{apiError}</div>
      <button onClick={() => window.location.reload()} style={{ marginTop:8, padding:"8px 16px", background:"#3b82f6", color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontSize:13 }}>Retry</button>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100vh", width:"100vw", fontFamily:"'Segoe UI', system-ui, sans-serif", overflow:"hidden" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 18px", background:"#fff", borderBottom:"1px solid #d1d5db", boxShadow:"0 1px 4px #0001", zIndex:30, flexShrink:0 }}>
        <span style={{ fontSize:16, fontWeight:600, color:"#374151" }}>🗺 Map Editor</span>
        <div style={{ flex:1 }} />
        {saving && <span style={{ fontSize:11, color:"#3b82f6" }}>💾 Saving...</span>}
        <button onClick={() => setShowBattle(b => !b)} style={{
          background: showBattle ? "#1f2937" : "#f9fafb",
          color: showBattle ? "#fff" : "#374151",
          border:"1px solid #d1d5db", borderRadius:6,
          padding:"5px 12px", fontSize:12, fontWeight:500, cursor:"pointer",
        }}>⚔ Battle</button>
        <span style={{ fontSize:11, color:"#9ca3af" }}>Scroll to zoom · Middle-click to pan · Draw: click points then <b>Finish</b> · Drag tokens to move</span>
      </div>

      <div style={{ display:"flex", flex:1, overflow:"hidden" }}>
        {/* Sidebar */}
        <div style={{ width:205, background:"#fff", borderRight:"1px solid #e5e7eb", display:"flex", flexDirection:"column", padding:12, gap:10, overflowY:"auto", flexShrink:0 }}>
          <ToolSection label="TOOLS">
            {[
              { id:"select",    icon:"↖", label:"Select / Move" },
              { id:"draw",      icon:"✏", label:"Draw Kingdom" },
              { id:"city",      icon:"●", label:"Place City" },
              { id:"character", icon:"👤", label:"Add Character" },
              { id:"army",      icon:"⚔", label:"Add Army" },
              { id:"pan",       icon:"✋", label:"Pan" },
            ].map(t => (
              <ToolBtn key={t.id} active={tool === t.id} onClick={() => { setTool(t.id); setDrawingPoints([]); }}>
                <span style={{ width:16, textAlign:"center" }}>{t.icon}</span> {t.label}
              </ToolBtn>
            ))}
          </ToolSection>

          {tool === "draw" && (
            <ToolSection label="KINGDOM COLOR">
              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                {KINGDOM_COLORS.map(c => (
                  <div key={c} onClick={() => setSelectedColor(c)} style={{
                    width:22, height:22, borderRadius:4, background:c, cursor:"pointer",
                    border: selectedColor === c ? "2px solid #374151" : "2px solid transparent",
                  }} />
                ))}
              </div>
              <div style={{ fontSize:11, color:"#6b7280", marginTop:4 }}>{drawingPoints.length} point{drawingPoints.length !== 1 ? "s" : ""} placed</div>
              {drawingPoints.length >= 3 && <button onClick={finishKingdom} style={{ ...mkBtn("#22c55e"), marginTop:4 }}>✓ Finish Kingdom</button>}
              {drawingPoints.length > 0  && <button onClick={() => setDrawingPoints([])} style={{ ...mkBtn("#9ca3af"), marginTop:4 }}>✕ Cancel</button>}
            </ToolSection>
          )}

          <ToolSection label="MAP">
            <ToolBtn onClick={() => { setPan({ x:0, y:0 }); setZoom(1); }}>⊞ Reset View</ToolBtn>
            <div style={{ fontSize:11, color:"#9ca3af", marginTop:2, lineHeight:1.8 }}>
              {kingdoms.length} kingdoms · {cities.length} cities<br />{characters.length} characters · {armies.length} armies
            </div>
          </ToolSection>

          {characters.length > 0 && (
            <ToolSection label="CHARACTERS">
              {characters.map(c => (
                <SidebarItem key={c.primary_key} color={c.color} label={c.name} shape="circle"
                  sub={c.city ? `In ${cities.find(ci => ci.primary_key === c.city)?.name || "city"}` : c.army ? `In ${armies.find(a => a.primary_key === c.army)?.name || "army"}` : null}
                  active={panel?.id === c.primary_key}
                  onClick={() => setPanel({ type:"character", id:c.primary_key })} />
              ))}
            </ToolSection>
          )}

          {armies.length > 0 && (
            <ToolSection label="ARMIES">
              {armies.map(a => (
                <SidebarItem key={a.primary_key} color={a.color} label={a.name} shape="diamond"
                  sub={a.city ? `In ${cities.find(ci => ci.primary_key === a.city)?.name || "city"}` : a.size ? `${a.size.toLocaleString()} troops` : null}
                  active={panel?.id === a.primary_key}
                  onClick={() => setPanel({ type:"army", id:a.primary_key })} />
              ))}
            </ToolSection>
          )}
        </div>

        {/* Canvas */}
        <div ref={containerRef} style={{ flex:1, overflow:"hidden", position:"relative" }}>
          <svg ref={svgRef} width="100%" height="100%"
            style={{ cursor:cursorStyle, display:"block", background:"#f0f2f5" }}
            onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp} onClick={handleSVGClick}>
            <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
              <rect x={0} y={0} width={CANVAS_W} height={CANVAS_H} fill="#f8f9fb" />
              {gridLines}
              <rect x={0} y={0} width={CANVAS_W} height={CANVAS_H} fill="none" stroke="#c9cdd4" strokeWidth="1.5" />

              {/* Kingdoms */}
              {kingdoms.map(k => {
                if (!k.points?.length) return null;
                const isEditing = editingKingdom?.id === k.primary_key;
                const displayPoints = isEditing ? editingKingdom.points : k.points;
                const pts = displayPoints.map(p => p.join(",")).join(" ");
                const isSel = panel?.type === "kingdom" && panel?.id === k.primary_key;
                const cx = displayPoints.reduce((a, p) => a + p[0], 0) / displayPoints.length;
                const cy = displayPoints.reduce((a, p) => a + p[1], 0) / displayPoints.length;
                return (
                  <g key={k.primary_key} style={{ cursor: isEditing ? "default" : "pointer" }}>
                    <polygon points={pts}
                      fill={k.color} fillOpacity={isEditing ? 0.15 : isSel ? 0.38 : 0.2}
                      stroke={k.color} strokeWidth={isEditing ? 2 : isSel ? 2.5 : 1.5}
                      strokeOpacity={isEditing ? 1 : isSel ? 1 : 0.6}
                      strokeDasharray={isEditing ? "6 3" : "none"}
                      style={{ filter: isSel && !isEditing ? `drop-shadow(0 0 5px ${k.color}88)` : "none" }} />
                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
                      fill="#1f2937" fontSize={14} fontWeight={600} style={{ pointerEvents:"none" }}
                      paintOrder="stroke" stroke="#f8f9fb" strokeWidth={3}>{k.name}</text>
                    {/* Vertex handles when editing */}
                    {isEditing && displayPoints.map((p, i) => (
                      <circle key={i} cx={p[0]} cy={p[1]} r={7}
                        fill={draggingVertex === i ? k.color : "#fff"}
                        stroke={k.color} strokeWidth={2}
                        style={{ cursor:"grab" }}
                        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setDraggingVertex(i); }} />
                    ))}
                  </g>
                );
              })}

              {/* In-progress drawing */}
              {drawingPoints.length > 0 && (
                <g>
                  {drawingPoints.length >= 2 && (
                    <polygon points={drawingPoints.map(p => p.join(",")).join(" ")}
                      fill={selectedColor} fillOpacity={0.1} stroke={selectedColor} strokeWidth={1.5} strokeDasharray="5 4" strokeOpacity={0.8} />
                  )}
                  {drawingPoints.map((p, i) => (
                    <circle key={i} cx={p[0]} cy={p[1]} r={4}
                      fill={i === 0 ? "#fff" : selectedColor} stroke={selectedColor} strokeWidth={1.5} />
                  ))}
                </g>
              )}

              {/* Cities */}
              {cities.map(c => {
                if (!c.coordinates) return null;
                const { x, y } = parseCoord(c.coordinates);
                const isSel = panel?.type === "city" && panel?.id === c.primary_key;
                const statusColor = STATUS_COLORS[c.status] || "#22c55e";
                const kColor = kingdoms.find(k => k.primary_key === c.allegiance)?.color;
                // Count chars/armies inside
                const inCount = characters.filter(ch => ch.city === c.primary_key).length
                              + armies.filter(a => a.city === c.primary_key).length;
                return (
                  <g key={c.primary_key} style={{ cursor:"pointer" }}>
                    {isSel && <circle cx={x} cy={y} r={14} fill="none" stroke="#374151" strokeWidth={1.5} strokeDasharray="3 2" opacity={0.6} />}
                    <circle cx={x} cy={y} r={7} fill={isSel ? "#111827" : "#374151"} stroke={kColor || "#f8f9fb"} strokeWidth={2} />
                    <circle cx={x+6} cy={y-6} r={3.5} fill={statusColor} stroke="#fff" strokeWidth={1} />
                    {inCount > 0 && (
                      <text x={x-8} y={y-7} textAnchor="middle" fill="#374151" fontSize={9} fontWeight={700} style={{ pointerEvents:"none" }}>{inCount}</text>
                    )}
                    <text x={x} y={y+18} textAnchor="middle" fill="#1f2937" fontSize={11} fontWeight={500}
                      style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={3}>{c.name}</text>
                  </g>
                );
              })}

              {/* Army tokens (map only — not in city) */}
              {mapArmies.map(a => {
                const isSel = panel?.type === "army" && panel?.id === a.primary_key;
                const isDrag = dragging?.id === a.primary_key && dragging?.type === "army";
                const { x, y } = tokenXY(a);
                const d = 10;
                const pts = `${x},${y-d} ${x+d},${y} ${x},${y+d} ${x-d},${y}`;
                return (
                  <g key={a.primary_key} onMouseDown={e => handleTokenMouseDown(e, "army", a.primary_key)}
                    style={{ cursor: tool === "select" ? "grab" : "default" }}>
                    {isSel && <polygon points={`${x},${y-d-5} ${x+d+5},${y} ${x},${y+d+5} ${x-d-5},${y}`}
                      fill="none" stroke={a.color} strokeWidth={1.5} strokeDasharray="3 2" />}
                    <polygon points={pts} fill={a.color} stroke="#fff" strokeWidth={1.5} opacity={isDrag ? 0.7 : 1} />
                    <text x={x} y={y+1} textAnchor="middle" dominantBaseline="middle"
                      fill="#fff" fontSize={8} fontWeight={700} style={{ pointerEvents:"none" }}>⚔</text>
                    <text x={x} y={y+18} textAnchor="middle" fill="#1f2937" fontSize={10} fontWeight={500}
                      style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={2}>{a.name}</text>
                    {a.size > 0 && <text x={x} y={y+27} textAnchor="middle" fill="#6b7280" fontSize={9}
                      style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={2}>{a.size.toLocaleString()}</text>}
                  </g>
                );
              })}

              {/* Character tokens (map only) */}
              {mapCharacters.map(c => {
                const isSel = panel?.type === "character" && panel?.id === c.primary_key;
                const isDrag = dragging?.id === c.primary_key && dragging?.type === "character";
                const { x, y } = tokenXY(c);
                return (
                  <g key={c.primary_key} onMouseDown={e => handleTokenMouseDown(e, "character", c.primary_key)}
                    style={{ cursor: tool === "select" ? "grab" : "default" }}>
                    {isSel && <circle cx={x} cy={y} r={13} fill="none" stroke={c.color} strokeWidth={1.5} strokeDasharray="3 2" />}
                    <circle cx={x} cy={y} r={8} fill={c.color} stroke="#fff" strokeWidth={2} opacity={isDrag ? 0.7 : 1} />
                    <text x={x} y={y+1} textAnchor="middle" dominantBaseline="middle"
                      fill="#fff" fontSize={9} fontWeight={700} style={{ pointerEvents:"none" }}>
                      {c.name.charAt(0).toUpperCase()}
                    </text>
                    <text x={x} y={y+18} textAnchor="middle" fill="#1f2937" fontSize={10} fontWeight={500}
                      style={{ pointerEvents:"none" }} paintOrder="stroke" stroke="#f8f9fb" strokeWidth={2}>{c.name}</text>
                  </g>
                );
              })}

              {/* Drag trail */}
              {dragging && dragPos && (() => {
                const src = dragging.type === "character"
                  ? characters.find(c => c.primary_key === dragging.id)
                  : armies.find(a => a.primary_key === dragging.id);
                if (!src?.coordinates) return null;
                const { x, y } = parseCoord(src.coordinates);
                return <line x1={x} y1={y} x2={dragPos[0]} y2={dragPos[1]}
                  stroke={src.color} strokeWidth={1} strokeDasharray="4 3" opacity={0.4} />;
              })()}
            </g>
          </svg>

          {/* Kingdom border edit overlay */}
          {editingKingdom && (
            <div style={{
              position:"absolute", top:12, left:"50%", transform:"translateX(-50%)",
              background:"#1e1b4b", borderRadius:10, padding:"10px 18px",
              display:"flex", alignItems:"center", gap:12, boxShadow:"0 4px 20px #0004", zIndex:25,
            }}>
              <span style={{ color:"#a5b4fc", fontSize:13 }}>✏ Drag vertices to move · Click edge to add point</span>
              <button onClick={confirmKingdomEdit}
                style={{ background:"#22c55e", color:"#fff", border:"none", borderRadius:6, padding:"5px 14px", cursor:"pointer", fontSize:13, fontWeight:600 }}>
                ✓ Confirm
              </button>
              <button onClick={cancelKingdomEdit}
                style={{ background:"#6b7280", color:"#fff", border:"none", borderRadius:6, padding:"5px 14px", cursor:"pointer", fontSize:13 }}>
                ✕ Cancel
              </button>
            </div>
          )}

          {/* Horizontal scrollbar */}
          {showHScroll && (
            <div style={{
              position:"absolute", bottom:0, left:0,
              width: showVScroll ? `calc(100% - ${SCROLL_W}px)` : "100%",
              height:SCROLL_W, background:"#e5e7eb", zIndex:15,
            }}>
              <div
                onMouseDown={e => handleScrollMouseDown(e, "h")}
                style={{
                  position:"absolute", top:2, height:SCROLL_W - 4, borderRadius:4,
                  left: hThumbX, width: hThumbW,
                  background:"#9ca3af", cursor:"pointer",
                  transition: scrollDrag.current ? "none" : "background 0.1s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#6b7280"}
                onMouseLeave={e => e.currentTarget.style.background = "#9ca3af"}
              />
            </div>
          )}

          {/* Vertical scrollbar */}
          {showVScroll && (
            <div style={{
              position:"absolute", top:0, right:0,
              height: showHScroll ? `calc(100% - ${SCROLL_W}px)` : "100%",
              width:SCROLL_W, background:"#e5e7eb", zIndex:15,
            }}>
              <div
                onMouseDown={e => handleScrollMouseDown(e, "v")}
                style={{
                  position:"absolute", left:2, width:SCROLL_W - 4, borderRadius:4,
                  top: vThumbY, height: vThumbH,
                  background:"#9ca3af", cursor:"pointer",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#6b7280"}
                onMouseLeave={e => e.currentTarget.style.background = "#9ca3af"}
              />
            </div>
          )}

          {/* Corner fill when both scrollbars visible */}
          {showHScroll && showVScroll && (
            <div style={{ position:"absolute", bottom:0, right:0, width:SCROLL_W, height:SCROLL_W, background:"#e5e7eb", zIndex:16 }} />
          )}

          {/* Zoom indicator */}
          <div style={{ position:"absolute", bottom: showHScroll ? SCROLL_W + 8 : 12, right: panel ? 316 + (showVScroll ? SCROLL_W : 0) : showVScroll ? SCROLL_W + 8 : 12, background:"#ffffffcc", border:"1px solid #e5e7eb", borderRadius:6, padding:"3px 10px", fontSize:12, color:"#6b7280" }}>
            {Math.round(zoom * 100)}%
          </div>

          {/* Detail panels */}
          {panelKingdom && <KingdomPanel kingdom={panelKingdom} characters={characters} armies={armies} cities={cities}
            onUpdate={updateKingdom} onClose={() => setPanel(null)} onDelete={() => deleteKingdom(panelKingdom.primary_key)} onEditBorders={startEditingKingdom} />}
          {panelCity && <CityPanel city={panelCity} characters={characters} armies={armies} kingdoms={kingdoms}
            onUpdate={updateCity} onClose={() => setPanel(null)} onDelete={() => deleteCity(panelCity.primary_key)} onMoveOut={handleMoveOut} />}
          {panelChar && <CharacterPanel character={panelChar} cities={cities} kingdoms={kingdoms} armies={armies}
            onUpdate={updateCharacter} onClose={() => setPanel(null)} onDelete={() => deleteCharacter(panelChar.primary_key)} />}
          {panelArmy && <ArmyPanel army={panelArmy} characters={characters} cities={cities} kingdoms={kingdoms}
            onUpdate={updateArmy} onClose={() => setPanel(null)} onDelete={() => deleteArmy(panelArmy.primary_key)} onMoveOut={handleMoveOut} />}
        </div>
      </div>

      {/* Name modal */}
      {modal && <NameModal type={modal.type} onConfirm={handleModalConfirm} onCancel={() => { setModal(null); setDrawingPoints([]); }} />}

      {/* Battle panel */}
      {showBattle && <BattlePanel />}
    </div>
  );
}


// ── Battle Simulator ─────────────────────────────────────────────────────────
function simulateBattle({ sizeA, skillA, sizeB, skillB, aIsAttacker, severity, aHasSurprise, bHasSurprise }) {
  const SURPRISE = 2.8;
  const effA = sizeA * Math.pow(skillA / 5, 2) * (aHasSurprise ? SURPRISE : 1);
  const effB = sizeB * Math.pow(skillB / 5, 2) * (bHasSurprise ? SURPRISE : 1);
  const randA = effA * (0.85 + Math.random() * 0.30);
  const randB = effB * (0.85 + Math.random() * 0.30);
  const aWins = randA >= randB;
  const ratio = Math.max(randA, randB) / Math.max(Math.min(randA, randB), 1);
  const d = Math.min(ratio, 2.5);
  // severity 1 = ~2-8% casualties across the board, severity 10 = ~40-80%
  const sevScale = severity / 10; // 0.1 – 1.0
  const baseLoser  = 0.05 + sevScale * 0.55; // 0.10 – 0.60
  const baseWinner = 0.02 + sevScale * 0.20; // 0.04 – 0.22
  const loserRate  = Math.min(0.90, baseLoser  + (d - 1) * 0.04 + Math.random() * 0.06);
  const winnerRate = Math.max(0.01, baseWinner - (d - 1) * 0.01 + Math.random() * 0.04);
  const casA = Math.round(sizeA * (aWins ? winnerRate : loserRate));
  const casB = Math.round(sizeB * (aWins ? loserRate  : winnerRate));
  return {
    winner: aWins ? "A" : "B",
    casualtiesA: casA, casualtiesB: casB,
    survivorsA: Math.max(0, sizeA - casA),
    survivorsB: Math.max(0, sizeB - casB),
    decisiveness: ratio,
  };
}

const SKILL_LABELS = ["","Rabble","Levy","Militia","Trained","Seasoned","Veteran","Elite","Champion","Legendary","Godlike"];

function verdictLabel(r) {
  if (r < 1.1) return "Pyrrhic Victory";
  if (r < 1.3) return "Narrow Victory";
  if (r < 1.7) return "Decisive Victory";
  if (r < 2.2) return "Crushing Victory";
  return "Total Annihilation";
}

const bInput = {
  background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:5,
  padding:"6px 10px", fontSize:13, color:"#111827", outline:"none",
  width:"100%", boxSizing:"border-box",
};

function SkillPips({ value, onChange }) {
  return (
    <div>
      <div style={{ display:"flex", gap:3 }}>
        {Array.from({length:10}, (_,i) => {
          const n = i + 1, on = n <= value;
          return (
            <div key={n} onClick={() => onChange(n)} style={{
              flex:1, height:20, borderRadius:3, cursor:"pointer",
              border:`1px solid ${on ? "#6b7280" : "#e5e7eb"}`,
              background: on ? (n<=3?"#ef4444":n<=6?"#f59e0b":"#22c55e") : "#f9fafb",
              transition:"all 0.1s",
            }} />
          );
        })}
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", marginTop:3 }}>
        <span style={{ fontSize:11, color:"#9ca3af" }}>1</span>
        <span style={{ fontSize:12, color:"#374151", fontWeight:600 }}>{value} — {SKILL_LABELS[value]}</span>
        <span style={{ fontSize:11, color:"#9ca3af" }}>10</span>
      </div>
    </div>
  );
}

function ArmyCol({ title, data, onChange, isAttacker, onSetAttacker, hasSurprise, onToggleSurprise }) {
  return (
    <div style={{ flex:1, minWidth:200 }}>
      <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>{title}</div>
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        <div>
          <div style={{ fontSize:11, color:"#9ca3af", marginBottom:3 }}>Name</div>
          <input style={bInput} placeholder="Army name..." value={data.name} onChange={e => onChange({...data, name:e.target.value})} />
        </div>
        <div>
          <div style={{ fontSize:11, color:"#9ca3af", marginBottom:3 }}>Troop Count</div>
          <input style={bInput} type="number" min={1} placeholder="e.g. 12000" value={data.size} onChange={e => onChange({...data, size:e.target.value})} />
        </div>
        <div>
          <div style={{ fontSize:11, color:"#9ca3af", marginBottom:3 }}>Skill Rating</div>
          <SkillPips value={data.skill} onChange={v => onChange({...data, skill:v})} />
        </div>
        <div>
          <div style={{ fontSize:11, color:"#9ca3af", marginBottom:3 }}>Role</div>
          <div style={{ display:"flex", gap:6 }}>
            {[["⚔ Attacker", true], ["🛡 Defender", false]].map(([label, val]) => (
              <button key={label} onClick={() => onSetAttacker(val)} style={{
                flex:1, padding:"6px", borderRadius:5, border:"1px solid #e5e7eb", cursor:"pointer",
                background: isAttacker === val ? "#1f2937" : "#f9fafb",
                color: isAttacker === val ? "#fff" : "#6b7280",
                fontSize:12, fontWeight:500,
              }}>{label}</button>
            ))}
          </div>
        </div>
        <div onClick={onToggleSurprise} style={{
          display:"flex", alignItems:"center", gap:8, padding:"7px 10px",
          border:`1px solid ${hasSurprise ? "#f59e0b" : "#e5e7eb"}`,
          borderRadius:5, cursor:"pointer",
          background: hasSurprise ? "#fffbeb" : "#f9fafb",
          transition:"all 0.15s",
        }}>
          <div style={{
            width:16, height:16, borderRadius:3, flexShrink:0,
            border:`2px solid ${hasSurprise ? "#f59e0b" : "#d1d5db"}`,
            background: hasSurprise ? "#f59e0b" : "transparent",
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>
            {hasSurprise && <span style={{ color:"#fff", fontSize:11, lineHeight:1 }}>✓</span>}
          </div>
          <span style={{ fontSize:12, fontWeight:500, color: hasSurprise ? "#92400e" : "#6b7280" }}>
            ⚡ Element of Surprise
          </span>
        </div>
      </div>
    </div>
  );
}

function BattlePanel() {
  const [armyA, setArmyA] = useState({ name:"", size:"", skill:5 });
  const [armyB, setArmyB] = useState({ name:"", size:"", skill:5 });
  const [aIsAttacker, setAIsAttacker] = useState(true);
  const [severity, setSeverity] = useState(5);
  const [aHasSurprise, setAHasSurprise] = useState(false);
  const [bHasSurprise, setBHasSurprise] = useState(false);
  const [result, setResult] = useState(null);

  const canSim = parseInt(armyA.size) > 0 && parseInt(armyB.size) > 0;
  const nameA = armyA.name || "Army A";
  const nameB = armyB.name || "Army B";

  function simulate() {
    const sA = parseInt(armyA.size)||0, sB = parseInt(armyB.size)||0;
    if (!sA || !sB) return;
    setResult(simulateBattle({ sizeA:sA, skillA:armyA.skill, sizeB:sB, skillB:armyB.skill, aIsAttacker, severity, aHasSurprise, bHasSurprise }));
  }

  return (
    <div style={{ background:"#fff", borderTop:"2px solid #e5e7eb", fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      <div style={{ padding:"12px 20px", borderBottom:"1px solid #f3f4f6", display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:14, fontWeight:700, color:"#111827" }}>⚔ Battle Simulator</span>
        <span style={{ fontSize:12, color:"#9ca3af" }}>Enter army details and simulate the outcome</span>
      </div>
      <div style={{ padding:"20px", display:"flex", gap:24, flexWrap:"wrap", alignItems:"flex-start" }}>
        <ArmyCol title="First Army" data={armyA} onChange={setArmyA} isAttacker={aIsAttacker} onSetAttacker={v => setAIsAttacker(v)} hasSurprise={aHasSurprise} onToggleSurprise={() => { setAHasSurprise(s => !s); setBHasSurprise(false); }} />
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", paddingTop:36, gap:6 }}>
          <div style={{ width:1, height:24, background:"#e5e7eb" }} />
          <span style={{ fontSize:12, fontWeight:700, color:"#9ca3af", letterSpacing:2 }}>VS</span>
          <div style={{ width:1, height:24, background:"#e5e7eb" }} />
        </div>
        <ArmyCol title="Second Army" data={armyB} onChange={setArmyB} isAttacker={!aIsAttacker} onSetAttacker={v => setAIsAttacker(!v)} hasSurprise={bHasSurprise} onToggleSurprise={() => { setBHasSurprise(s => !s); setAHasSurprise(false); }} />
        <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:12 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>Battle Severity</div>
            <div style={{ display:"flex", gap:3, marginBottom:3 }}>
              {Array.from({length:10}, (_,i) => {
                const n = i + 1, on = n <= severity;
                return (
                  <div key={n} onClick={() => setSeverity(n)} style={{
                    flex:1, height:20, borderRadius:3, cursor:"pointer",
                    border:`1px solid ${on ? "#6b7280" : "#e5e7eb"}`,
                    background: on ? (n<=3?"#86efac":n<=6?"#fbbf24":"#ef4444") : "#f9fafb",
                    transition:"all 0.1s",
                  }} />
                );
              })}
            </div>
            <div style={{ display:"flex", justifyContent:"space-between" }}>
              <span style={{ fontSize:11, color:"#9ca3af" }}>1</span>
              <span style={{ fontSize:12, color:"#374151", fontWeight:600 }}>{severity} — {["Skirmish","Minor Clash","Engagement","Bloody Fight","Hard Battle","Brutal Contest","Savage Battle","Carnage","Slaughter","Bloodbath"][severity-1]}</span>
              <span style={{ fontSize:11, color:"#9ca3af" }}>10</span>
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={simulate} disabled={!canSim} style={{
              background: canSim ? "#1f2937" : "#e5e7eb", color: canSim ? "#fff" : "#9ca3af",
              border:"none", borderRadius:6, padding:"9px 24px", fontSize:13, fontWeight:600,
              cursor: canSim ? "pointer" : "not-allowed",
            }}>⚔ Simulate Battle</button>
            {result && <button onClick={() => setResult(null)} style={{
              background:"none", border:"1px solid #e5e7eb", borderRadius:6,
              padding:"9px 14px", fontSize:12, color:"#6b7280", cursor:"pointer",
            }}>↺ Reset</button>}
          </div>
          {result && (() => {
            const stats = [
              { name:nameA, size:parseInt(armyA.size), cas:result.casualtiesA, surv:result.survivorsA, won:result.winner==="A" },
              { name:nameB, size:parseInt(armyB.size), cas:result.casualtiesB, surv:result.survivorsB, won:result.winner==="B" },
            ];
            return (
              <div style={{ border:"1px solid #e5e7eb", borderRadius:8, overflow:"hidden", maxWidth:600 }}>
                <div style={{ background:"#1f2937", padding:"10px 16px", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ color:"#f9fafb", fontWeight:700, fontSize:13 }}>🏆 {result.winner==="A"?nameA:nameB} wins</span>
                  <span style={{ color:"#fbbf24", fontSize:12 }}>— {verdictLabel(result.decisiveness)}</span>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr" }}>
                  {stats.map((s,i) => (
                    <div key={i} style={{ padding:"14px 16px", borderRight:i===0?"1px solid #f3f4f6":"none", background:s.won?"#f0fdf4":"#fff" }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:10 }}>{s.won?"⚔":"🏳"} {s.name}</div>
                      {[
                        ["Started",   s.size.toLocaleString(),                "#374151"],
                        ["Fallen",    `−${s.cas.toLocaleString()}`,            "#ef4444"],
                        ["Remaining", s.surv.toLocaleString(),                 s.won?"#16a34a":"#374151"],
                        ["Loss Rate", `${Math.round((s.cas/s.size)*100)}%`,    "#9ca3af"],
                      ].map(([label,val,color]) => (
                        <div key={label} style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                          <span style={{ fontSize:12, color:"#9ca3af" }}>{label}</span>
                          <span style={{ fontSize:13, fontWeight:600, color }}>{val}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ── Sidebar helpers ───────────────────────────────────────────────────────────
function SidebarItem({ color, label, shape, sub, active, onClick }) {
  return (
    <div onClick={onClick} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", padding:"3px 4px", borderRadius:4, background: active ? "#f3f4f6" : "transparent" }}>
      <span style={{ width:10, height:10, background:color, display:"inline-block", flexShrink:0, borderRadius: shape === "circle" ? "50%" : 2, transform: shape === "diamond" ? "rotate(45deg)" : "none" }} />
      <div style={{ overflow:"hidden" }}>
        <div style={{ fontSize:12, color:"#374151", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{label}</div>
        {sub && <div style={{ fontSize:10, color:"#9ca3af" }}>{sub}</div>}
      </div>
    </div>
  );
}
function ToolSection({ label, children }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
      <div style={{ fontSize:10, letterSpacing:1.5, color:"#9ca3af", fontWeight:600, borderBottom:"1px solid #f3f4f6", paddingBottom:4, marginBottom:2 }}>{label}</div>
      {children}
    </div>
  );
}
function ToolBtn({ children, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: active ? "#f3f4f6" : "transparent",
      border: `1px solid ${active ? "#d1d5db" : "transparent"}`,
      color: active ? "#111827" : "#4b5563",
      borderRadius:5, padding:"5px 10px", cursor:"pointer", fontSize:13,
      textAlign:"left", display:"flex", alignItems:"center", gap:6,
      transition:"all 0.12s", fontWeight: active ? 500 : 400,
    }}>
      {children}
    </button>
  );
}